/**
 * Format number with currency and abbreviations
 * @param {number} value - The numeric value to format
 * @param {string} displayFormat - Format type: 'currency_abbreviated', 'currency', 'number_abbreviated', 'number', 'percentage', 'score'
 * @param {string} unit - Unit symbol (e.g., '€', '$', '%', 'score')
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, displayFormat, unit = '') => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }

  const numValue = parseFloat(value);
  const absValue = Math.abs(numValue);
  const sign = numValue < 0 ? '-' : '';

  let formattedValue;
  let suffix = '';

  // Handle percentage format
  if (displayFormat === 'percentage') {
    // Percentages are stored as decimals (0.1 = 10%), so multiply by 100
    const percentageValue = absValue * 100;
    // For percentages, show 1-2 decimal places, no thousand separators
    if (percentageValue % 1 === 0) {
      formattedValue = percentageValue.toFixed(0);
    } else {
      formattedValue = percentageValue.toFixed(1);
      // Remove trailing zero if present
      formattedValue = formattedValue.replace(/\.0$/, '');
    }
    return `${sign}${formattedValue}%`;
  }

  // Handle score format (0-100 range, simple number)
  if (displayFormat === 'score') {
    // For scores, show 1 decimal place if needed, no thousand separators
    if (absValue % 1 === 0) {
      formattedValue = absValue.toFixed(0);
    } else {
      formattedValue = absValue.toFixed(1);
      // Remove trailing zero if present
      formattedValue = formattedValue.replace(/\.0$/, '');
    }
    // Append unit if provided (e.g., "score"), otherwise just the number
    return unit ? `${sign}${formattedValue} ${unit}` : `${sign}${formattedValue}`;
  }

  // Determine abbreviation and scale
  if (displayFormat === 'currency_abbreviated' || displayFormat === 'number_abbreviated') {
    if (absValue >= 1000000) {
      formattedValue = (absValue / 1000000).toFixed(3);
      suffix = 'M';
    } else if (absValue >= 1000) {
      formattedValue = (absValue / 1000).toFixed(3);
      suffix = 'k';
    } else {
      formattedValue = absValue.toFixed(2);
      // Remove trailing zeros for values < 1000
      formattedValue = formattedValue.replace(/\.?0+$/, '');
    }
    
    // For k and M suffixes, only remove trailing zeros if all 3 decimals are zero
    if (suffix && formattedValue.endsWith('.000')) {
      formattedValue = formattedValue.replace(/\.000$/, '');
    }
  } else if (displayFormat === 'number') {
    // Standard number formatting - use thousand separators for large numbers
    if (absValue >= 1000) {
      formattedValue = absValue.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    } else {
      // For small numbers, show 1-2 decimal places
      if (absValue % 1 === 0) {
        formattedValue = absValue.toFixed(0);
      } else {
        formattedValue = absValue.toFixed(2);
        formattedValue = formattedValue.replace(/\.0+$/, '');
      }
    }
  } else {
    // Default: currency format with thousand separators
    formattedValue = absValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  // Add currency symbol if needed
  let result = `${sign}${formattedValue}${suffix}`;
  
  if (displayFormat === 'currency_abbreviated' || displayFormat === 'currency') {
    // Use unit if provided, otherwise default to €
    const currencySymbol = unit || '€';
    result = `${result} ${currencySymbol}`;
  } else if (unit && displayFormat !== 'currency_abbreviated' && displayFormat !== 'currency' && displayFormat !== 'percentage') {
    // For non-currency, non-percentage formats, append unit if provided
    // Don't add % if unit already contains it
    if (unit.includes('%')) {
      result = `${result}${unit}`;
    } else {
      result = `${result} ${unit}`;
    }
  }

  return result;
};

/**
 * Format number for chart tooltips (simpler format)
 * @param {number} value - The numeric value to format
 * @param {string} displayFormat - Format type
 * @param {string} unit - Unit symbol
 * @returns {string} Formatted number string
 */
export const formatNumberForChart = (value, displayFormat, unit = '') => {
  return formatNumber(value, displayFormat, unit);
};

